import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Button,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
  IconButton,
  
  Card,
  CardContent,
  
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  
  Schedule as ScheduleIcon,
  
  Category as CategoryIcon,
  
  Tune as TuneIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { debounce } from 'lodash';
import { useCurrency } from '../../context/CurrencyContext';

const SearchAndFilter = ({ 
  searchType = 'jobs', // 'jobs' or 'services'
  onSearchResults,
  onFiltersChange,
  initialFilters = {}
}) => {
  const { currencyCode, formatCurrency } = useCurrency();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    category: '',
    employmentType: '', // For jobs
    experienceLevel: '', // For jobs
    salaryRange: [0, 100000], // For jobs
    priceRange: [0, 10000], // For services
    companySize: '', // For jobs
    serviceType: '', // For services
    availability: '', // For services
    rating: 0,
    datePosted: '',
    skills: [],
    benefits: [], // For jobs
    tags: [], // For services
    ...initialFilters
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);

  // Predefined options
  const jobCategories = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Marketing',
    'Sales', 'Engineering', 'Design', 'Customer Service', 'Operations',
    'Human Resources', 'Legal', 'Consulting', 'Manufacturing', 'Retail'
  ];

  const categories = [
    'Web Development', 'Mobile Development', 'UI/UX Design', 'Graphic Design',
    'Digital Marketing', 'Content Writing', 'SEO Services', 'Data Analysis',
    'Consulting', 'Photography', 'Video Editing', 'Translation',
    'Virtual Assistant', 'Accounting', 'Legal Services', 'Tutoring',
    'Fitness Training', 'Music Lessons', 'Home Services', 'Other'
  ];

  const employmentTypes = [
    'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Remote'
  ];

  const experienceLevels = [
    'Entry Level', 'Mid Level', 'Senior Level', 'Executive', 'No Experience Required'
  ];

  const companySizes = [
    'Startup (1-10)', 'Small (11-50)', 'Medium (51-200)', 
    'Large (201-1000)', 'Enterprise (1000+)'
  ];

  const serviceTypes = [
    'One-time', 'Recurring', 'Project-based', 'Consultation', 'Emergency'
  ];

  const availabilityOptions = [
    'Immediate', 'Within 24 hours', 'Within a week', 'Flexible'
  ];

  const datePostedOptions = [
    { label: 'Any time', value: '' },
    { label: 'Last 24 hours', value: '1' },
    { label: 'Last 3 days', value: '3' },
    { label: 'Last week', value: '7' },
    { label: 'Last month', value: '30' }
  ];

  const sortOptions = searchType === 'jobs' ? [
    { label: 'Relevance', value: 'relevance' },
    { label: 'Date Posted', value: 'datePosted' },
    { label: 'Salary', value: 'salary' },
    { label: 'Company Rating', value: 'companyRating' },
    { label: 'Experience Level', value: 'experienceLevel' }
  ] : [
    { label: 'Relevance', value: 'relevance' },
    { label: 'Date Posted', value: 'datePosted' },
    { label: 'Price', value: 'price' },
    { label: 'Rating', value: 'rating' },
    { label: 'Reviews Count', value: 'reviewsCount' }
  ];

  // Debounced search handler
  const performSearch = useCallback(async (query, currentFilters) => {
    setLoading(true);
    try {
      const searchParams = {
        query: query.trim(),
        filters: currentFilters,
        sortBy,
        sortOrder,
        type: searchType
      };

      // Call the search function passed from parent
      if (onSearchResults) {
        await onSearchResults(searchParams);
      }

      // Notify parent about filter changes
      if (onFiltersChange) {
        onFiltersChange(currentFilters);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, searchType, onSearchResults, onFiltersChange]);

  useEffect(() => {
    const handler = debounce((query, currentFilters) => {
      performSearch(query, currentFilters);
    }, 500);
    handler(searchQuery, filters);
    return () => {
      if (handler.cancel) handler.cancel();
    };
  }, [searchQuery, filters, performSearch]);

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
  };

  const handleSkillsChange = (event, newValue) => {
    handleFilterChange('skills', newValue);
  };

  const handleBenefitsChange = (benefit) => {
    const currentBenefits = filters.benefits || [];
    const newBenefits = currentBenefits.includes(benefit)
      ? currentBenefits.filter(b => b !== benefit)
      : [...currentBenefits, benefit];
    handleFilterChange('benefits', newBenefits);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      location: '',
      category: '',
      employmentType: '',
      experienceLevel: '',
      salaryRange: [0, 100000],
      priceRange: [0, 10000],
      companySize: '',
      serviceType: '',
      availability: '',
      rating: 0,
      datePosted: '',
      skills: [],
      benefits: [],
      tags: []
    };
    setFilters(clearedFilters);
    setSearchQuery('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'salaryRange' || key === 'priceRange') {
        const [min, max] = value;
        if (min > 0 || max < (key === 'salaryRange' ? 100000 : 10000)) count++;
      } else if (Array.isArray(value) && value.length > 0) {
        count++;
      } else if (value && value !== '' && value !== 0) {
        count++;
      }
    });
    return count;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* Main Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={`Search ${searchType}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchQuery('')} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Quick Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Location"
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              startAdornment={<CategoryIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="">All Categories</MenuItem>
              {(searchType === 'jobs' ? jobCategories : categories).map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {searchType === 'jobs' && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={filters.employmentType}
                onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                startAdornment={<WorkIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="">All Types</MenuItem>
                {employmentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {searchType === 'services' && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={filters.serviceType}
                onChange={(e) => handleFilterChange('serviceType', e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {serviceTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Date Posted</InputLabel>
            <Select
              value={filters.datePosted}
              onChange={(e) => handleFilterChange('datePosted', e.target.value)}
              startAdornment={<ScheduleIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              {datePostedOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Filter Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<TuneIcon />}
            onClick={() => setShowAdvanced(!showAdvanced)}
            variant={showAdvanced ? 'contained' : 'outlined'}
          >
            Advanced Filters
            {getActiveFiltersCount() > 0 && (
              <Chip 
                size="small" 
                label={getActiveFiltersCount()} 
                sx={{ ml: 1 }}
                color="primary"
              />
            )}
          </Button>
          
          {getActiveFiltersCount() > 0 && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              variant="text"
              color="secondary"
            >
              Clear All
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={sortOrder === 'desc'}
                onChange={(e) => setSortOrder(e.target.checked ? 'desc' : 'asc')}
              />
            }
            label="Desc"
          />
        </Box>
      </Box>

      {/* Advanced Filters */}
      {showAdvanced && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Advanced Filters
            </Typography>
            
            <Grid container spacing={3}>
              {/* Salary/Price Range */}
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  {searchType === 'jobs' ? `Salary Range (${currencyCode})` : `Price Range (${currencyCode})`}
                </Typography>
                <Slider
                  value={searchType === 'jobs' ? filters.salaryRange : filters.priceRange}
                  onChange={(e, newValue) => 
                    handleFilterChange(searchType === 'jobs' ? 'salaryRange' : 'priceRange', newValue)
                  }
                  valueLabelDisplay="auto"
                  min={0}
                  max={searchType === 'jobs' ? 100000 : 10000}
                  step={searchType === 'jobs' ? 1000 : 100}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">
                    {formatCurrency(searchType === 'jobs' ? filters.salaryRange[0] : filters.priceRange[0])}
                  </Typography>
                  <Typography variant="caption">
                    {formatCurrency(searchType === 'jobs' ? filters.salaryRange[1] : filters.priceRange[1])}
                  </Typography>
                </Box>
              </Grid>

              {/* Experience Level (Jobs only) */}
              {searchType === 'jobs' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Experience Level</InputLabel>
                    <Select
                      value={filters.experienceLevel}
                      onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                    >
                      <MenuItem value="">Any Level</MenuItem>
                      {experienceLevels.map((level) => (
                        <MenuItem key={level} value={level}>
                          {level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Company Size (Jobs only) */}
              {searchType === 'jobs' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Size</InputLabel>
                    <Select
                      value={filters.companySize}
                      onChange={(e) => handleFilterChange('companySize', e.target.value)}
                    >
                      <MenuItem value="">Any Size</MenuItem>
                      {companySizes.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Availability (Services only) */}
              {searchType === 'services' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Availability</InputLabel>
                    <Select
                      value={filters.availability}
                      onChange={(e) => handleFilterChange('availability', e.target.value)}
                    >
                      <MenuItem value="">Any Time</MenuItem>
                      {availabilityOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Skills */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={filters.skills}
                  onChange={handleSkillsChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      const { key, ...rest } = tagProps;
                      return (
                        <Chip
                          key={key ?? index}
                          variant="outlined"
                          label={option}
                          {...rest}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Skills"
                      placeholder="Add skills..."
                    />
                  )}
                />
              </Grid>

              {/* Benefits (Jobs only) */}
              {searchType === 'jobs' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Benefits
                  </Typography>
                  <FormGroup row>
                    {['Health Insurance', 'Dental Insurance', 'Retirement Plan', 'Paid Time Off', 
                      'Flexible Schedule', 'Remote Work', 'Professional Development', 'Gym Membership'].map((benefit) => (
                      <FormControlLabel
                        key={benefit}
                        control={
                          <Checkbox
                            checked={filters.benefits?.includes(benefit) || false}
                            onChange={() => handleBenefitsChange(benefit)}
                          />
                        }
                        label={benefit}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Searching...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SearchAndFilter;